import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { StyledCartAddedPopup } from './style';
import { Icon } from 'ui';
import { FallbackImage } from 'ui/UploadImage/fallBack/FallBackImage';
import { useCartState } from 'public/context/CartContext';
import {
  calculateBasePrice,
  isRenderablePrice,
} from 'utils/productTotalPriceHelper';
import useMarketType from 'utils/useMarketType';
import PublicRoute from 'enums/publicRoutes';
import { options } from 'config/config';

const DEFAULT_IMAGE = options.images.defaultProductImage;
const POPUP_DURATION_MS = 5000;

interface CartAddedPopupProps {
  suppressed?: boolean;
  onOpenCart?: () => void;
}

const CartAddedPopup = ({ suppressed, onOpenCart }: CartAddedPopupProps) => {
  const [visible, setVisible] = useState(false);
  const intl = useIntl();
  const location = useLocation();
  const { isEmarket } = useMarketType();
  const { lastAddedItem } = useCartState();

  useEffect(() => {
    if (!lastAddedItem) {
      return;
    }

    setVisible(true);
    const timeout = setTimeout(() => setVisible(false), POPUP_DURATION_MS);

    return () => clearTimeout(timeout);
  }, [lastAddedItem]);

  const isCartPage =
    location.pathname === PublicRoute.CART_PAGE ||
    location.pathname === PublicRoute.WHOLESALE_CART;

  if (!visible || !lastAddedItem || suppressed || isCartPage) {
    return null;
  }

  const { product, amount, addedAt } = lastAddedItem;

  const productAmount = product.product_amounts?.find((productAmount) =>
    isEmarket
      ? productAmount.type === 'EMARKET'
      : productAmount.type !== 'EMARKET'
  );

  const { changedBasePrice, storageStockDiscountPrice } = calculateBasePrice(
    productAmount!,
    amount
  );

  const unitPrice =
    isRenderablePrice(storageStockDiscountPrice) &&
    isRenderablePrice(changedBasePrice) &&
    storageStockDiscountPrice < changedBasePrice
      ? storageStockDiscountPrice
      : changedBasePrice;

  const packageUnitVal = Number(productAmount?.package_unit_val);
  const packageUnitPrice = Number(productAmount?.package_unit_price);
  const packagePrice =
    productAmount?.storage_unit === 'iep' &&
    packageUnitVal > 0 &&
    packageUnitPrice > 0
      ? packageUnitVal * packageUnitPrice
      : null;

  const effectivePrice =
    isRenderablePrice(unitPrice) && unitPrice > 0 ? unitPrice : packagePrice;

  const displayAmount = parseFloat(amount.toFixed(3));

  return (
    <StyledCartAddedPopup
      className={!isEmarket ? 'wholesale' : ''}
      onClick={onOpenCart}
    >
      <div className={'cart-added-popup-header'}>
        <div className={'cart-added-popup-icon'} key={addedAt}>
          <Icon name={'shopping-cart-black'} />
          <svg className={'countdown-ring'} viewBox="0 0 46 46">
            <circle
              className={'countdown-ring-track'}
              cx="23"
              cy="23"
              r="21.5"
            />
            <circle
              className={'countdown-ring-progress'}
              cx="23"
              cy="23"
              r="21.5"
            />
          </svg>
        </div>
        <div className={'cart-added-popup-title'}>
          {intl.formatMessage({ id: 'cart.added_popup_title' })}
        </div>
      </div>
      <div className={'cart-added-popup-divider'} />
      <div className={'cart-added-popup-product'}>
        <FallbackImage
          src={
            product.image_small
              ? product.image_small
              : product.farm?.logo
                ? product.farm.logo
                : DEFAULT_IMAGE
          }
          type={'ProductCard'}
          preview={false}
        />
        <div className={'cart-added-popup-product-info'}>
          <div className={'cart-added-popup-product-name'}>{product.name}</div>
          {effectivePrice !== null && (
            <div className={'cart-added-popup-product-price'}>
              {displayAmount} x{' '}
              <b>
                {effectivePrice.toFixed(2)} € /{productAmount?.storage_unit}
              </b>{' '}
              = <b>{(effectivePrice * amount).toFixed(2)} €</b>
            </div>
          )}
        </div>
      </div>
    </StyledCartAddedPopup>
  );
};

export default CartAddedPopup;
